import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { DEFAULT_PAGE_SIZE } from '../constants/feedback.constants';
import {
  feedbackListItemSchema,
  paginatedFeedbacksSchema,
  type FeedbackListItem,
  type FeedbackListParams,
  type PaginatedFeedbacks,
  type CompleteFeedbackPayload,
} from '../types/feedback.types';

export const feedbackQueryKeys = {
  all: () => ['feedbacks'] as const,
  lists: () => [...feedbackQueryKeys.all(), 'list'] as const,
  listInfinite: (params: FeedbackListParams) =>
    [...feedbackQueryKeys.lists(), 'infinite', params] as const,
  detail: (id: number) => [...feedbackQueryKeys.all(), 'detail', id] as const,
};

export const feedbackDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: feedbackQueryKeys.detail(id),
    queryFn: async (): Promise<FeedbackListItem> => {
      const res = await apiClient.get(API.feedback.detail(id));
      return feedbackListItemSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const feedbackListInfiniteQueryOptions = (params: FeedbackListParams) =>
  infiniteQueryOptions({
    queryKey: feedbackQueryKeys.listInfinite(params),
    queryFn: async ({ pageParam }): Promise<PaginatedFeedbacks> => {
      const res = await apiClient.get(API.feedback.list, {
        params: { ...params, page: pageParam, limit: DEFAULT_PAGE_SIZE },
      });
      return paginatedFeedbacksSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function markFeedbackRead(id: number): Promise<void> {
  await apiClient.patch(API.feedback.markRead(id));
}

export async function completeFeedback(
  id: number,
  payload: CompleteFeedbackPayload,
): Promise<void> {
  await apiClient.patch(API.feedback.complete(id), payload);
}
